import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, ArrowRight, MapPin, User, Leaf, ShoppingCart, Info, Award, Crown, Quote } from 'lucide-react';

const MangoesCatalogue = () => {
  const [selectedVariety, setSelectedVariety] = useState(null);

  const mangoVarieties = [
    {
      id: 'alphonso',
      name: 'Alphonso (Hapus)',
      subtitle: 'The King of Mangoes - GI Tagged',
      image: 'https://images.pexels.com/photos/4687187/pexels-photo-4687187.jpeg?auto=compress&cs=tinysrgb&w=1200',
      price: '₹800-1200',
      unit: 'per dozen',
      minOrder: '50 dozen',
      season: 'April - June',
      origin: 'Ratnagiri, Maharashtra',
      giTagged: true,
      organic: true,
      grade: 'GI Tagged Premium',
      brix: '16-18°',
      weight: '150-300g per piece',
      description: 'The world-famous Ratnagiri Alphonso - celebrated for its rich, creamy texture, non-fibrous flesh, and unmatched aroma. GI-tagged to protect its authenticity. The most sought-after mango variety globally.',
      features: ['GI Tagged', 'Non-fibrous', 'Rich aroma', 'Creamy texture', 'Premium export', 'Naturally ripened'],
      farmer: {
        name: 'Ganesh Vishnu Sawant',
        location: 'Devgad, Ratnagiri',
        image: 'https://images.unsplash.com/photo-1608366911553-3296e6f5e2ef?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=400',
        experience: '35 years',
        farmSize: '8 acres',
        story: 'Our family has grown Alphonso mangoes for four generations on these coastal hills. The sea breeze and laterite soil give Devgad Hapus its unique taste. But climate change is real - flowering patterns are shifting, cyclones are more frequent. SPANXPORT helped us install weather stations, get crop insurance, and reach markets that pay for true Ratnagiri quality. Without them, we would be at the mercy of local traders who mix inferior mangoes with our premium harvest.'
      },
      certifications: ['GI Tagged', 'Organic India', 'APEDA Certified'],
      exportMarkets: ['USA', 'UK', 'UAE', 'Japan', 'Singapore']
    },
    {
      id: 'kesar',
      name: 'Kesar Mango',
      subtitle: 'Queen of Mangoes - Gujarat GI',
      image: 'https://images.pexels.com/photos/5097708/pexels-photo-5097708.jpeg?auto=compress&cs=tinysrgb&w=1200',
      price: '₹500-700',
      unit: 'per dozen',
      minOrder: '100 dozen',
      season: 'May - July',
      origin: 'Junagadh, Gujarat',
      giTagged: true,
      organic: false,
      grade: 'GI Tagged Grade A',
      brix: '18-22°',
      weight: '200-350g per piece',
      description: 'Known for its intense saffron-colored flesh and exceptional sweetness. Gir Kesar is GI-tagged and considered the second-most premium Indian mango. Perfect for pulp, desserts, and fresh consumption.',
      features: ['GI Tagged', 'Saffron flesh', 'High sweetness', 'Pulp-grade available', 'Extended season'],
      farmer: {
        name: 'Bhavesh Khimji Gohil',
        location: 'Talala, Junagadh',
        image: 'https://images.pexels.com/photos/29912210/pexels-photo-29912210.jpeg?auto=compress&cs=tinysrgb&w=400',
        experience: '25 years',
        farmSize: '15 acres',
        story: 'Gir forest is famous for lions, but we Kesar farmers are its unsung guardians. Our orchards provide buffer zones for wildlife. The challenge was always getting fair prices - middlemen exploited our isolation. SPANXPORT set up a collection center, trained us on carbide-free ripening, and now our mangoes carry QR codes that buyers can trace. Last year, a customer in London sent us a thank-you video. That moment made decades of struggle worthwhile.'
      },
      certifications: ['GI Tagged', 'APEDA', 'FSSAI'],
      exportMarkets: ['USA', 'UK', 'UAE', 'Canada']
    },
    {
      id: 'dasheri',
      name: 'Dasheri Mango',
      subtitle: 'North India\'s Pride',
      image: 'https://images.unsplash.com/photo-1715678771914-eef55b1d2f26?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200',
      price: '₹300-450',
      unit: 'per dozen',
      minOrder: '100 dozen',
      season: 'June - July',
      origin: 'Lucknow, Uttar Pradesh',
      giTagged: false,
      organic: true,
      grade: 'Premium',
      brix: '20-24°',
      weight: '150-250g per piece',
      description: 'The legendary Dasheri from Lucknow - known for its intensely sweet, fiber-free flesh and distinctive elongated shape. A favorite in North India and increasingly popular in export markets.',
      features: ['Fiber-free', 'Intense sweetness', 'Aromatic', 'Traditional variety', 'Good shelf life'],
      farmer: {
        name: 'Mohammad Irfan Khan',
        location: 'Malihabad, Lucknow',
        image: 'https://images.unsplash.com/photo-1709532918611-77db9df9b27d?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=400',
        experience: '40 years',
        farmSize: '28 acres',
        story: 'Malihabad has grown Dasheri for over 200 years. My grandfather planted trees that still bear fruit. But the youth were leaving - mango farming seemed like a dying profession. SPANXPORT changed that narrative. They helped us get organic certification, connected us with Dubai importers, and now my sons see a future here. We\'re not just selling mangoes anymore - we\'re selling the heritage of Awadh.'
      },
      certifications: ['Organic India', 'UP State Organic', 'APEDA'],
      exportMarkets: ['UAE', 'Saudi Arabia', 'Kuwait', 'Bahrain']
    },
    {
      id: 'langra',
      name: 'Langra Mango',
      subtitle: 'The Seedless Wonder',
      image: 'https://images.unsplash.com/photo-1714040292671-14e9722d1a09?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200',
      price: '₹350-500',
      unit: 'per dozen',
      minOrder: '100 dozen',
      season: 'July - August',
      origin: 'Varanasi, Uttar Pradesh',
      giTagged: false,
      organic: false,
      grade: 'Grade A',
      brix: '18-22°',
      weight: '200-300g per piece',
      description: 'Named after its origin story, Langra is loved for its green skin even when ripe and its distinctive tangy-sweet taste. The small seed gives more flesh per fruit. A monsoon favorite.',
      features: ['Green when ripe', 'Small seed', 'Tangy-sweet', 'Monsoon variety', 'High pulp ratio'],
      farmer: {
        name: 'Raghunath Dubey',
        location: 'Varanasi, UP',
        image: 'https://images.pexels.com/photos/3339871/pexels-photo-3339871.jpeg?auto=compress&cs=tinysrgb&w=400',
        experience: '32 years',
        farmSize: '20 acres',
        story: 'Varanasi is the spiritual capital of India, and Langra mango is part of our offerings to the divine. But farming here is challenging - floods damage orchards every few years. SPANXPORT helped us with flood-resistant rootstocks and connected us with buyers who appreciate the unique Langra taste. When I see pilgrims enjoying our mangoes at the ghats, I know our work is blessed.'
      },
      certifications: ['FSSAI', 'APEDA'],
      exportMarkets: ['Nepal', 'Bangladesh', 'UAE']
    },
    {
      id: 'totapuri',
      name: 'Totapuri Mango',
      subtitle: 'The Processing Champion',
      image: 'https://images.pexels.com/photos/2294471/pexels-photo-2294471.jpeg?auto=compress&cs=tinysrgb&w=1200',
      price: '₹150-250',
      unit: 'per dozen',
      minOrder: '500 dozen',
      season: 'May - July',
      origin: 'Karnataka & Andhra Pradesh',
      giTagged: false,
      organic: false,
      grade: 'Processing Grade',
      brix: '14-16°',
      weight: '350-500g per piece',
      description: 'The backbone of India\'s mango pulp industry. Totapuri\'s large size, firm flesh, and distinctive beak shape make it ideal for processing. Also excellent for pickles, chutneys, and raw mango preparations.',
      features: ['Large size', 'Processing ideal', 'Pickle-grade', 'Bulk availability', 'Consistent quality'],
      farmer: {
        name: 'Yellappa Krishnappa',
        location: 'Srinivaspur, Karnataka',
        image: 'https://images.pexels.com/photos/6476384/pexels-photo-6476384.jpeg?auto=compress&cs=tinysrgb&w=400',
        experience: '22 years',
        farmSize: '35 acres',
        story: 'Totapuri is not glamorous like Alphonso, but it feeds the world\'s mango pulp demand. We used to sell at whatever price the processor offered - sometimes below our cost. SPANXPORT aggregated small farmers like us, negotiated directly with pulp factories, and now we get advance contracts with guaranteed prices. My children no longer feel ashamed to say their father is a Totapuri farmer.'
      },
      certifications: ['FSSAI', 'BRC (for processing)', 'APEDA'],
      exportMarkets: ['Middle East', 'Europe (pulp)', 'USA (pulp)']
    },
    {
      id: 'banganapalli',
      name: 'Banganapalli (Benishan)',
      subtitle: 'Andhra\'s Golden Gift',
      image: 'https://images.unsplash.com/photo-1711182073834-f25e17086e48?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200',
      price: '₹250-400',
      unit: 'per dozen',
      minOrder: '100 dozen',
      season: 'April - June',
      origin: 'Kurnool, Andhra Pradesh',
      giTagged: true,
      organic: true,
      grade: 'GI Tagged Grade A',
      brix: '17-20°',
      weight: '250-400g per piece',
      description: 'The pride of Andhra Pradesh - known for its large size, golden-yellow color, and mild sweetness. GI-tagged Banganapalli is perfect for those who prefer less-intense sweetness. Excellent for fresh consumption and smoothies.',
      features: ['GI Tagged', 'Large size', 'Mild sweetness', 'Golden color', 'Thin skin'],
      farmer: {
        name: 'Venkateshwarlu Reddy',
        location: 'Banganapalle, Kurnool',
        image: 'https://images.unsplash.com/photo-1709207513502-d9242031ab1b?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=400',
        experience: '28 years',
        farmSize: '22 acres',
        story: 'Banganapalli town gave its name to this mango, but ironically, local farmers were the poorest. Traders from other states controlled the market. SPANXPORT\'s GI certification support helped us protect our heritage. Now, we have a farmer collective of 150 members, our own grading facility, and export contracts. The mango that was named after our village is finally enriching our village.'
      },
      certifications: ['GI Tagged', 'Organic India', 'APEDA'],
      exportMarkets: ['UAE', 'Singapore', 'Malaysia']
    }
  ];

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="relative h-[450px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/918643/pexels-photo-918643.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="Mango orchards India"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#263776]/95 via-[#263776]/80 to-transparent"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <Link to="/products/orivana-fresh" className="inline-flex items-center text-white/80 hover:text-white mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to ORIVANA Fresh
          </Link>
          <Badge className="bg-amber-500 text-white mb-4 block w-fit">
            <Crown className="w-3 h-3 mr-1 inline" /> The King of Fruits
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Indian Mangoes</h1>
          <p className="text-lg md:text-xl text-white/90 max-w-2xl">
            GI-tagged premium varieties from India's finest orchards. Alphonso, Kesar, Dasheri and more - direct from farmers with full traceability.
          </p>
          <div className="mt-6 flex flex-wrap gap-4">
            <Badge className="bg-white/20 text-white px-4 py-2">6 Premium Varieties</Badge>
            <Badge className="bg-white/20 text-white px-4 py-2">GI Tagged Options</Badge>
            <Badge className="bg-white/20 text-white px-4 py-2">Carbide-Free Ripening</Badge>
          </div>
        </div>
      </section>

      {/* Trust Banner */}
      <section className="bg-gradient-to-r from-amber-500 to-orange-500 py-4">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center gap-6 md:gap-12 text-white text-sm flex-wrap">
            <span className="flex items-center"><Award className="w-4 h-4 mr-2" /> India's Largest Mango Exporter</span>
            <span className="hidden md:inline">|</span>
            <span>Naturally Ripened</span>
            <span className="hidden md:inline">|</span>
            <span>Quality & Transparency Assured</span>
          </div>
        </div>
      </section>

      {/* Varieties Grid */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#263776] mb-4">Select Your Variety</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              From the royal Alphonso to the processing champion Totapuri - each variety tells a story of tradition and farmer dedication
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left: Varieties List */}
            <div className="lg:col-span-1">
              <div className="space-y-4">
                {mangoVarieties.map((variety) => (
                  <Card 
                    key={variety.id}
                    className={`cursor-pointer transition-all duration-300 ${
                      selectedVariety?.id === variety.id 
                        ? 'ring-2 ring-amber-500 shadow-lg bg-amber-50' 
                        : 'hover:shadow-md hover:bg-slate-50'
                    }`}
                    onClick={() => setSelectedVariety(variety)}
                    data-testid={`mango-variety-${variety.id}`}
                  >
                    <CardContent className="p-4 flex items-center gap-4">
                      <img 
                        src={variety.image} 
                        alt={variety.name}
                        className="w-20 h-20 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-bold text-[#263776]">{variety.name}</h3>
                          {variety.giTagged && (
                            <Badge className="bg-amber-100 text-amber-700 text-xs">GI</Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">{variety.origin}</p>
                        <p className="text-amber-600 font-bold mt-1">{variety.price} <span className="text-xs text-slate-400">{variety.unit}</span></p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-slate-400" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Right: Selected Variety Details */}
            <div className="lg:col-span-2">
              {selectedVariety ? (
                <div className="sticky top-4">
                  <div className="relative rounded-xl overflow-hidden h-[350px] mb-6">
                    <img 
                      src={selectedVariety.image} 
                      alt={selectedVariety.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 left-4 flex gap-2 flex-wrap">
                      <Badge className="bg-[#263776] text-white">{selectedVariety.grade}</Badge>
                      {selectedVariety.giTagged && (
                        <Badge className="bg-amber-500 text-white">
                          <Award className="w-3 h-3 mr-1" /> GI Tagged
                        </Badge>
                      )}
                      {selectedVariety.organic && (
                        <Badge className="bg-green-600 text-white">
                          <Leaf className="w-3 h-3 mr-1" /> Organic
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h2 className="text-3xl font-bold text-[#263776] mb-2">{selectedVariety.name}</h2>
                      <p className="text-lg text-amber-600 font-medium mb-4">{selectedVariety.subtitle}</p>
                      <p className="text-slate-600 mb-4">{selectedVariety.description}</p>
                      
                      <div className="bg-slate-50 rounded-lg p-4 mb-4">
                        <h4 className="font-bold text-[#263776] mb-3 flex items-center">
                          <Info className="w-4 h-4 mr-2" /> Specifications
                        </h4>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div><span className="text-slate-500">Season:</span> <span className="font-medium">{selectedVariety.season}</span></div>
                          <div><span className="text-slate-500">Origin:</span> <span className="font-medium">{selectedVariety.origin}</span></div>
                          <div><span className="text-slate-500">Brix:</span> <span className="font-medium">{selectedVariety.brix}</span></div>
                          <div><span className="text-slate-500">Weight:</span> <span className="font-medium">{selectedVariety.weight}</span></div>
                          <div><span className="text-slate-500">Min Order:</span> <span className="font-medium">{selectedVariety.minOrder}</span></div>
                          <div><span className="text-slate-500">Grade:</span> <span className="font-medium">{selectedVariety.grade}</span></div>
                        </div>
                      </div>

                      {selectedVariety.exportMarkets && (
                        <div className="mb-4">
                          <h4 className="font-bold text-[#263776] mb-2">Export Markets</h4>
                          <div className="flex flex-wrap gap-2">
                            {selectedVariety.exportMarkets.map((market, idx) => (
                              <Badge key={idx} className="bg-amber-100 text-amber-700">
                                {market}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="mb-4">
                        <h4 className="font-bold text-[#263776] mb-2">Key Features</h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedVariety.features.map((feature, idx) => (
                            <Badge key={idx} variant="outline" className="border-[#263776]/30">
                              {feature}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h4 className="font-bold text-[#263776] mb-2">Certifications</h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedVariety.certifications.map((cert, idx) => (
                            <Badge key={idx} className="bg-[#263776]/10 text-[#263776]">
                              {cert}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div>
                      <Card className="mb-6 border-2 border-amber-200 bg-amber-50/50">
                        <CardContent className="p-6">
                          <div className="text-3xl font-bold text-amber-600 mb-1">{selectedVariety.price}</div>
                          <div className="text-slate-500 mb-4">{selectedVariety.unit} (Wholesale)</div>
                          <div className="text-sm text-slate-600 mb-4">
                            Minimum Order: <span className="font-bold">{selectedVariety.minOrder}</span>
                          </div>
                          <Button asChild className="w-full bg-amber-500 hover:bg-amber-600 mb-3">
                            <Link to={`/products/orivana-fresh/enquiry?product=${selectedVariety.id}&category=mangoes`}>
                              <ShoppingCart className="w-4 h-4 mr-2" />
                              Request Quote
                            </Link>
                          </Button>
                          <Button asChild variant="outline" className="w-full border-[#263776] text-[#263776]">
                            <Link to="/contact">
                              Contact Sales Team
                            </Link>
                          </Button>
                        </CardContent>
                      </Card>

                      {/* Farmer Story */}
                      <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
                        <CardContent className="p-6">
                          <h4 className="font-bold text-[#263776] mb-4 flex items-center">
                            <User className="w-5 h-5 mr-2 text-amber-600" /> Meet the Farmer
                          </h4>
                          <div className="flex items-start gap-4 mb-4">
                            <img 
                              src={selectedVariety.farmer.image}
                              alt={selectedVariety.farmer.name}
                              className="w-20 h-20 rounded-full object-cover border-2 border-amber-300"
                            />
                            <div>
                              <h5 className="font-bold text-[#263776]">{selectedVariety.farmer.name}</h5>
                              <div className="flex items-center text-sm text-slate-500 mb-1">
                                <MapPin className="w-3 h-3 mr-1" />
                                {selectedVariety.farmer.location}
                              </div>
                              <div className="text-xs text-slate-400">
                                {selectedVariety.farmer.experience} experience • {selectedVariety.farmer.farmSize}
                              </div>
                            </div>
                          </div>
                          <div className="relative">
                            <Quote className="w-6 h-6 text-amber-300 absolute -top-2 -left-1" />
                            <p className="text-sm text-slate-700 pl-6 italic leading-relaxed">
                              {selectedVariety.farmer.story}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-[500px] bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border-2 border-dashed border-amber-200">
                  <div className="text-center p-8">
                    <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Crown className="w-10 h-10 text-amber-500" />
                    </div>
                    <h3 className="text-xl font-bold text-[#263776] mb-2">Select a Variety</h3>
                    <p className="text-slate-500 max-w-sm">Click on any mango variety to view detailed specifications, farmer story, and pricing</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-gradient-to-r from-amber-500 to-orange-500">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Your Single Stop for Premium Indian Mangoes</h2>
          <p className="text-white/90 mb-6 max-w-xl mx-auto">
            Quality assured, transparency guaranteed. From farm to your table with complete traceability.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild size="lg" className="bg-white text-amber-600 hover:bg-amber-50">
              <Link to="/products/orivana-fresh/enquiry?category=mangoes">Get Export Quote</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
              <Link to="/verified-directory/apply-farmer">Partner as Farmer</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MangoesCatalogue;
